// Single source for what a group card calls itself.
//
// Contract: the assignment row exactly as the API returns it, plus the bundle it
// belongs to. Callers hand over the data and get back finished display strings —
// no view decides any of this for itself.
//
//   resolveGroupCardTitle(assignment, bundle)
//
// A card is titled by what it teaches, never by how it is filtered:
//
//   lesson bundle, core  → the lesson's name
//   anything else        → the CCSS short name, else its code
//
// An LC filter shows up beside the code instead — "2 Learning Components", hovering
// the full LC titles — so differently-filtered cards still read differently without
// the filter hijacking the title (Figma 4914-72908: "Keep the Standard title as the
// card title instead of replacing it with the selected Learning Component(s)").
// Standard-flow cards also drop the Core/Prerequisite/Extension tag: the CCSS code
// already says what the group is, and the flow has no lesson for the tracks to be
// relative to.
//
// The lesson itself arrives on the bundle as `lesson` (LessonDetailOut, exactly as
// the API sends it); the name and the compact G6U3L2 code are derived from it here.

// ---- Lesson code ---------------------------------------------------------
// A lesson core card is labelled "G6U3L2" and hovers "Grade 6 Unit 3 Lesson 2".
// Both are derived here from the lesson's own course/unit/lesson fields rather
// than handed in pre-built, so no caller has to know the format.

// First run of digits in a free-form ordinal ("Unit 3" -> "3"). "" when there is
// none, e.g. grade "K".
const numToken = (s) => (String(s ?? "").match(/\d+/) || [""])[0];

// ponytail: first-integer parse; if IM ever ships non-"Unit N" ordinals, this is
// the one knob to revisit.
const lessonCodeParts = (lesson) => {
  if (!lesson) return null;
  // Digitless grade labels (K, HS) keep their own text rather than losing the
  // slot, which would read "GU3L2".
  const g = numToken(lesson.course_grade_level)
    || numToken(lesson.course_name)
    || String(lesson.course_grade_level ?? "").trim();
  const u = numToken(lesson.unit_ordinal_name);
  const l = numToken(lesson.ordinal_name);
  return g && u && l ? { g, u, l } : null;
};

// Null when any part is unreadable, so the card falls back to its standard codes
// rather than rendering a malformed "GUL".
export const lessonCode = (lesson) => {
  const p = lessonCodeParts(lesson);
  return p ? `G${p.g}U${p.u}L${p.l}` : null;
};

// The same parts spelled out, so the code and its hover can never disagree.
export const lessonCodeLabel = (lesson) => {
  const p = lessonCodeParts(lesson);
  return p ? `Grade ${p.g} Unit ${p.u} Lesson ${p.l}` : null;
};

// lc_name is null when the id resolves in neither name table; show the id rather
// than an empty line. The count stays correct either way.
export const lcName = (lc) => lc?.lc_name || lc?.lc_id || "";

// Title case follows the Figma frames ("1 Learning Component" / "5 Learning
// Components"); the ticket prose uses lowercase. Figma is the newer artifact.
export const lcCountLabel = (learningComponents = []) => {
  const n = learningComponents.length;
  if (!n) return null;
  return `${n} Learning Component${n === 1 ? "" : "s"}`;
};

// Hover contents for the "X Learning Components" label: one entry per selected LC,
// in the teacher's stored selection order — deliberately NOT sorted, the LC1/LC2
// prefixes are positional and must survive a reload. A single LC drops the number.
export const lcTooltipItems = (learningComponents = []) =>
  learningComponents.map((lc, i) => ({
    label: learningComponents.length === 1 ? "LC:" : `LC${i + 1}:`,
    name: lcName(lc),
  }));

// Preview-link label. A group pointing at a saved set shows "My Question Set" and
// its question_count is that set's size; otherwise the filter decides the label.
export const questionSetLabel = (questionFilter, questionCount, questionSetId = null) => {
  const base = questionSetId ? "My Question Set" : questionFilter ? "Filtered Questions" : "Full Question Bank";
  return Number.isInteger(questionCount) ? `${base} (${questionCount})` : base;
};

// The lesson flow's track enum is singular; the card's styling keys are plural.
const displayTrack = (t) => (t === "extension" ? "extensions" : t);

/**
 * Everything a group card needs to name itself.
 *
 * @param {object} assignment  A GroupAssignmentOut row, verbatim: group_code,
 *   track_type, standard_code, standard_codes, standard_name, question_filter,
 *   learning_components, question_count.
 * @param {object} bundle  Its bundle: bundle_type ("lesson" | "standard") and, on
 *   lesson bundles, `lesson` — the LessonDetailOut the API returns. The core card's
 *   name and its G6U3L2 code are derived from that, not handed in.
 *
 * @returns {{
 *   code: string, codeTooltip: string|null, title: string,
 *   standard: { code: string|null, codes: string[]|null, name: string|null },
 *   learningComponents: Array<object>, countLabel: string|null,
 *   tooltipItems: Array<{label: string, name: string}>,
 *   trackType: string, showTrackTag: boolean, questionSetLabel: string,
 *   isFiltered: boolean, titledBy: "lesson" | "standard",
 * }}
 */
export const resolveGroupCardTitle = (assignment = {}, bundle = {}) => {
  const {
    track_type: rawTrack,
    standard_code: standardCode = null,
    standard_codes: standardCodes = null,
    standard_name: standardName = null,
    question_filter: questionFilter = null,
    question_set_id: questionSetId = null,
    learning_components: rawLcs,
    question_count: questionCount,
  } = assignment;

  const trackType = displayTrack(rawTrack);
  const bundleType = bundle?.bundle_type;
  const isLessonCore = bundleType === "lesson" && trackType === "core";
  const lcs = rawLcs || [];

  const usesLcs = lcs.length > 0;
  // A lesson's core card is the lesson, not the standards it happens to address.
  const lesson = bundle?.lesson || null;
  const fromLesson = isLessonCore ? (lesson?.name || null) : null;

  return {
    // Small uppercase line: the lesson's compact code on a core card, else the
    // standards this group covers.
    code: (isLessonCore && lessonCode(lesson))
      || (standardCodes?.length ? standardCodes.join(", ") : standardCode)
      || "—",
    codeTooltip: (isLessonCore && lessonCodeLabel(lesson)) || null,
    title: fromLesson || standardName || standardCode || "—",
    standard: { code: standardCode, codes: standardCodes, name: standardName },
    learningComponents: lcs,
    countLabel: usesLcs ? lcCountLabel(lcs) : null,
    tooltipItems: usesLcs ? lcTooltipItems(lcs) : [],
    trackType,
    // A CCSS-assigned (standard-flow) card carries no Core/Prereq/Extension tag;
    // lesson cards keep theirs, "Other" included. A missing bundle_type means a
    // standard-based bundle, so only an explicit lesson bundle shows the tag.
    showTrackTag: bundleType === "lesson",
    questionSetLabel: questionSetLabel(questionFilter, questionCount, questionSetId),
    isFiltered: Boolean(questionFilter),
    titledBy: fromLesson ? "lesson" : "standard",
  };
};
